import React, { useState, useCallback, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useDropzone } from "react-dropzone";

const CommentModal = ({
  open,
  onClose,
  commentType,
  setCommentType,
  newComment,
  setNewComment,
  selectedFiles,
  setSelectedFiles,
  onSave,
}) => {
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [confirmExternalOpen, setConfirmExternalOpen] = useState(false);
  const [pendingType, setPendingType] = useState("internal");

  useEffect(() => {
  if (open) {
    setCommentType("internal");
  }
}, [open, setCommentType]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
    },
    [setSelectedFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
  });

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCommentTypeChange = (e) => {
    const selectedType = e.target.value;
    if (selectedType === "external") {
      setPendingType(selectedType);
      setConfirmExternalOpen(true);
    } else {
      setCommentType(selectedType);
    }
  };

  const confirmExternalYes = () => {
    setCommentType(pendingType);
    setConfirmExternalOpen(false);
  };

  const confirmExternalCancel = () => {
    setCommentType("internal");
    setConfirmExternalOpen(false);
  };

  const handleSave = async () => {
    if (!newComment.trim()) {
      setSnackbar({
        open: true,
        message: "Comment cannot be empty",
        severity: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      await onSave();
      setSnackbar({
        open: true,
        message: "Comment added successfully",
        severity: "success",
      });
      onClose();
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to add comment",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.98)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
  };

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "92%", sm: 520 },
            maxHeight: "90vh",
            overflowY: "auto",
            backgroundColor: "rgba(255,255,255,0.98)",
            padding: 0,
            borderRadius: "18px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
            border: "1px solid rgba(47,99,173,0.08)",
          }}
        >
          <Box
            sx={{
              px: 3,
              pt: 2.5,
              pb: 1.5,
              borderBottom: "1px solid rgba(47,99,173,0.08)",
              backgroundColor: "rgba(47,99,173,0.02)",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              Add Comment
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
              Add an internal or external comment and optionally attach files.
            </Typography>
          </Box>

          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Comment"
              multiline
              rows={4}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ ...textFieldSx, mb: 2 }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Comment Type</InputLabel>
              <Select
                value={commentType}
                onChange={handleCommentTypeChange}
                label="Comment Type"
                sx={textFieldSx}
              >
                <MenuItem value="external">External</MenuItem>
                <MenuItem value="internal">Internal</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 600, color: "#223142" }}
              >
                Attach Files
              </Typography>

              <Paper
                variant="outlined"
                {...getRootProps()}
                sx={{
                  p: 2.5,
                  border: "2px dashed #90caf9",
                  borderRadius: "14px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: isDragActive ? "#e3f2fd" : "#fafafa",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f5faff",
                    borderColor: "#1976d2",
                  },
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 40, color: "#1976d2", mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {isDragActive
                    ? "Drop files here..."
                    : "Drag & drop files here, or click to select"}
                </Typography>
              </Paper>

              {selectedFiles.length > 0 && (
                <List
                  dense
                  sx={{
                    mt: 1.25,
                    maxHeight: 120,
                    overflowY: "auto",
                    border: "1px solid rgba(47,99,173,0.08)",
                    borderRadius: "12px",
                    backgroundColor: "rgba(47,99,173,0.02)",
                    px: 1,
                  }}
                >
                  {selectedFiles.map((file, idx) => (
                    <ListItem key={idx} sx={{ borderBottom: "1px solid #eee" }}>
                      <ListItemText
                        primary={file.name}
                        primaryTypographyProps={{
                          noWrap: true,
                          sx: { color: "#223142", fontWeight: 500 },
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFile(idx)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.25 }}>
              <Button variant="outlined" onClick={onClose} sx={secondaryButtonSx}>
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={primaryButtonSx}
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {saving ? "Saving..." : "Save Comment"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={confirmExternalOpen}
        onClose={confirmExternalCancel}
        PaperProps={{ sx: dialogPaperSx }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            color: "#223142",
            pt: 3,
          }}
        >
          <WarningAmberIcon sx={{ color: "#ed6c02" }} />
          Send to Homeowner?
        </DialogTitle>

        <DialogContent>
          <Typography>
            This external comment will also be sent to the homeowner via email.
            Are you sure you want to continue?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1.25 }}>
          <Button onClick={confirmExternalCancel} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>
          <Button onClick={confirmExternalYes} variant="contained" sx={primaryButtonSx}>
            Yes, Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CommentModal;